import { useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import getPageMetadata from '~/components/helmetSeo/utils'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { ESiteImagesTypes } from '~/features/partners/models'
import { EClientPath } from '~/models/api'

function HelmetSeo() {
  const { partner, isLoading, location } = usePartnerCentralized()

  const [faviconUrl, setFaviconUrl] = useState<string | null>(null)
  const [ogImageUrl, setOgImageUrl] = useState<string | null>(null)

  useEffect(() => {
    if (isLoading || !partner?.siteImages) return

    const favicon = partner.siteImages.find(
      (img) => img.type === ESiteImagesTypes.favicon,
    )?.imageUrl
    const ogImage = partner.siteImages.find(
      (img) => img.type === ESiteImagesTypes.ogImage,
    )?.imageUrl

    if (favicon) {
      setFaviconUrl(favicon)
    }
    if (ogImage) {
      setOgImageUrl(ogImage)
    }
  }, [partner, isLoading])

  const partnerName = useMemo(() => {
    if (partner?.partnerName) {
      return partner.partnerName
    }
    return 'AI Assistant'
  }, [partner])

  const { title, description } = getPageMetadata(
    location.pathname as EClientPath,
    partnerName,
  )

  const currentUrl = window.location.href || ''
  const currentHostname = window.location.hostname || ''

  if (isLoading && !title) {
    return (
      <Helmet>
        <title>Loading...</title>
      </Helmet>
    )
  }
  if (!title || !description) {
    const defaultMeta = getPageMetadata(EClientPath.home, partnerName)
    return (
      <Helmet>
        <title>{defaultMeta.title}</title>
        <meta
          name="description"
          content={defaultMeta.description}
          data-rh="true"
        />
      </Helmet>
    )
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} data-rh="true" />

      {faviconUrl && <link rel="icon" href={faviconUrl} data-rh="true" />}

      {/* Open Graph meta tags */}
      <meta property="og:title" content={title} data-rh="true" />
      <meta property="og:description" content={description} data-rh="true" />
      <meta property="og:image" content={ogImageUrl || ''} data-rh="true" />
      <meta property="og:url" content={currentUrl} data-rh="true" />
      <meta property="og:type" content="website" data-rh="true" />
      {partnerName && (
        <meta property="og:site_name" content={partnerName} data-rh="true" />
      )}

      {/* Twitter meta tags */}
      <meta name="twitter:card" content="summary_large_image" data-rh="true" />
      <meta name="twitter:title" content={title} data-rh="true" />
      <meta name="twitter:description" content={description} data-rh="true" />
      <meta name="twitter:image" content={ogImageUrl || ''} data-rh="true" />
      {currentHostname && (
        <meta
          property="twitter:domain"
          content={currentHostname}
          data-rh="true"
        />
      )}
      {currentUrl && (
        <meta property="twitter:url" content={currentUrl} data-rh="true" />
      )}
      {/* Optional: Add twitter:site if you have partner-specific handles */}
      {/* {partner?.twitterHandle && <meta name="twitter:site" content={partner.twitterHandle} data-rh="true" />} */}
    </Helmet>
  )
}

export default HelmetSeo
